import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { type Shipment } from '../../domain/Shipment'
import { downloadBOL } from '../../use-cases/downloadBOL'
import { downloadRatecon } from '../../use-cases/downloadRatecon'
import { getShipment } from '../../use-cases/getShipment'
import {
  updateShipmentStatus,
  type UpdateShipmentStatusParams,
} from '../../use-cases/updateShipmentStatus'

type ShipmentState = {
  shipment?: Shipment
  shipmentStatus: RequestStatus
  statusBOL: RequestStatus
  statusRatecon: RequestStatus
}

const initialShipmentState = {
  shipmentStatus: RequestStatus.Initial,
  statusBOL: RequestStatus.Initial,
  statusRatecon: RequestStatus.Initial,
}

export function useShipment() {
  const [{ shipment, shipmentStatus, statusBOL, statusRatecon }, setShipmentState] =
    useState<ShipmentState>(initialShipmentState)

  const fetchShipment = useCallback(async (uuid: string, { signal }: AbortController) => {
    try {
      setShipmentState(previous => ({ ...previous, shipmentStatus: RequestStatus.Pending }))

      const response = await getShipment(uuid, { signal })

      setShipmentState(previous => ({
        ...previous,
        shipment: response,
        shipmentStatus: RequestStatus.Done,
      }))
    } catch (error) {
      if (!signal.aborted) {
        setShipmentState(previous => ({
          ...previous,
          shipment: undefined,
          shipmentStatus: RequestStatus.Error,
        }))
      }
    }
  }, [])

  const refetchShipment = useCallback(async (uuid: string) => {
    try {
      const result = await getShipment(uuid)

      setShipmentState(previous => ({
        ...previous,
        shipment: result,
        shipmentStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setShipmentState(previous => ({
        ...previous,
        shipmentStatus: RequestStatus.Error,
      }))
    }
  }, [])

  const submitShipmentStatus = useCallback(
    async (shipmentStatusPayload: UpdateShipmentStatusParams) => {
      await updateShipmentStatus(shipmentStatusPayload)
    },
    []
  )

  const fetchBOL = useCallback(async (uuid: string) => {
    try {
      setShipmentState(previous => ({ ...previous, statusBOL: RequestStatus.Pending }))

      const result = await downloadBOL(uuid)
      return result
    } finally {
      setShipmentState(previous => ({ ...previous, statusBOL: RequestStatus.Done }))
    }
  }, [])

  const fetchRatecon = useCallback(async (uuid: string) => {
    try {
      setShipmentState(previous => ({ ...previous, statusRatecon: RequestStatus.Pending }))
      const result = await downloadRatecon(uuid)
      return result
    } finally {
      setShipmentState(previous => ({ ...previous, statusRatecon: RequestStatus.Done }))
    }
  }, [])

  const downloadingBOL = statusBOL === RequestStatus.Pending
  const downloadingRatecon = statusRatecon === RequestStatus.Pending

  return {
    shipment,
    shipmentStatus,
    downloadingBOL,
    downloadingRatecon,
    submitShipmentStatus,
    fetchShipment,
    refetchShipment,
    fetchBOL,
    fetchRatecon,
  }
}
